import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../store/auth';
import { api } from '../lib/api';
import { TwilioConfigSection } from './settings/TwilioConfigSection';
import { AiConfigSection } from './settings/AiConfigSection';
import { AiPromptsSection } from './settings/AiPromptsSection';
import { AiBulkAnalyzeSection } from './settings/AiBulkAnalyzeSection';
import { AiCleanupLeadSection } from './settings/AiCleanupLeadSection';
import { AiJobsSection } from './settings/AiJobsSection';
import { NumberPoolSection } from './settings/NumberPoolSection';
import { UserManagementSection } from './settings/UserManagementSection';
import { WhatsappConfigSection } from './settings/WhatsappConfigSection';
import { VoiplineConfigSection } from './settings/VoiplineConfigSection';

interface UserRow {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'admin' | 'supervisor' | 'agent';
  active: boolean;
  skills: string[];
  state: string;
}

export function Settings(): JSX.Element {
  const me = useAuthStore((s) => s.user);
  const isSupervisor = me?.role === 'supervisor';
  const isAdmin = me?.role === 'admin';

  // Supervisors see a read-only users table — fetch only for them
  const supervisorUsers = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<UserRow[]>('/users'),
    enabled: isSupervisor,
  });

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Settings</h1>
        <p className="text-sm text-ink-500">Account, users, numbers, and Twilio configuration.</p>
      </header>

      {/* Account */}
      <section className="card p-4 space-y-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-500">Your account</h2>
        <p className="text-sm">
          Signed in as <strong>{me?.firstName} {me?.lastName}</strong> ({me?.email}) — role{' '}
          <strong>{me?.role}</strong>.
        </p>
      </section>

      {/* Admin-only sections */}
      {isAdmin && (
        <>
          <TwilioConfigSection />
          <VoiplineConfigSection />
          <WhatsappConfigSection />
          <AiConfigSection />
          <AiPromptsSection />
          <AiBulkAnalyzeSection />
          <AiCleanupLeadSection />
          <AiJobsSection />
          <UserManagementSection />
          <NumberPoolSection />
        </>
      )}

      {/* Supervisors: read-only users table */}
      {isSupervisor && (
        <section className="card overflow-hidden">
          <header className="border-b border-ink-200 px-4 py-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-500">Users</h2>
          </header>
          <div className="overflow-x-auto">
          <table className="w-full text-sm whitespace-nowrap">
            <thead className="bg-ink-50 text-left text-xs uppercase tracking-wider text-ink-500">
              <tr>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Email</th>
                <th className="px-4 py-2">Role</th>
                <th className="px-4 py-2">State</th>
                <th className="px-4 py-2">Skills</th>
              </tr>
            </thead>
            <tbody>
              {supervisorUsers.data?.map((u) => (
                <tr key={u.id} className="border-t border-ink-100">
                  <td className="px-4 py-2">{u.firstName} {u.lastName}</td>
                  <td className="px-4 py-2">{u.email}</td>
                  <td className="px-4 py-2 capitalize">{u.role}</td>
                  <td className="px-4 py-2 capitalize">{u.state}</td>
                  <td className="px-4 py-2 text-xs">{u.skills.join(', ') || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </section>
      )}
    </div>
  );
}
